// Navigator Action Planning Service - Control generation for Navigator assessments
// Uses Navigator-specific Redux state (navigatorRisks, not explorerRisks or modelRisks)
import {
  saveControl,
  updateControlEvaluationField
} from '../../redux/actions/controlActions';
import { PolicyMatchingService } from '../../autopilotMode/services/PolicyMatchingService';
import { authFetch } from '../../utils/authFetch';

export class NavigatorActionPlanningService {
  constructor(dispatch, modelId, assessmentUuid, liveUpdateCallback = null) {
    this.dispatch = dispatch;
    this.modelId = modelId;
    this.assessmentUuid = assessmentUuid;
    this.liveUpdateCallback = liveUpdateCallback;
  }

  async executeActionPlanning(areaKey, areaDefinition, contextData, templateConfig, inheritedRisks, progressCallback) {
    try {
      console.log(`[NavigatorActionPlanningService] Starting action planning for area: ${areaKey}`);
      if (progressCallback) progressCallback('actions');
      await new Promise(resolve => setTimeout(resolve, 600));

      // Get fresh Redux state
      const getState = () => this.dispatch((dispatch, getState) => getState());
      const currentState = getState();

      // Access Navigator risks (not explorerRisks or modelRisks)
      const navigatorRisks = currentState.riskItems?.byAssessment?.[this.assessmentUuid]?.navigatorRisks || {};
      const areaRisks = navigatorRisks[areaKey]?.areaRisks || {};

      const evaluatedRisks = [];

      console.log(`[NavigatorActionPlanningService] State source for area: ${areaKey}`);
      console.log(`[NavigatorActionPlanningService] Assessment UUID:`, this.assessmentUuid);
      console.log(`[NavigatorActionPlanningService] Available risk categories:`, Object.keys(areaRisks));

      Object.entries(areaRisks).forEach(([category, riskArray]) => {
        console.log(`[NavigatorActionPlanningService] Checking category "${category}": ${Array.isArray(riskArray) ? riskArray.length : 'not array'} risks`);
        if (Array.isArray(riskArray)) {
          riskArray.forEach((risk, idx) => {
            console.log(`[NavigatorActionPlanningService] Risk ${idx}: "${risk.riskName}" - status: ${risk.status}, priority: ${risk.priority}`);
            if (risk.status === 'Saved' && risk.priority) {
              console.log(`[NavigatorActionPlanningService] FOUND evaluated risk for action planning: "${risk.riskName}"`);
              evaluatedRisks.push({
                ...risk,
                areaKey,
                category
              });
            }
          });
        }
      });

      let actionsGenerated = 0;

      const usePolicyRAG = templateConfig?.use_control_rag === true;
      const policyTypes = templateConfig?.policy_types || ['governance', 'compliance', 'operational'];
      const riskNarrative = contextData?.riskNarrative || '';

      console.log('[NavigatorActionPlanningService] Template RAG config:', {
        use_control_rag: usePolicyRAG,
        policy_types: policyTypes,
        has_narrative: !!riskNarrative
      });

      for (const risk of evaluatedRisks) {
        try {
          console.log(`[NavigatorActionPlanningService] Generating controls via AI for risk: ${risk.riskName}`);

          const controlsResponse = await authFetch('/.netlify/functions/navigatorGenerateControls', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              risk: {
                riskId: risk.riskId,
                riskName: risk.riskName,
                areaName: areaKey,
                areaKey: areaKey,
                priority: risk.priority,
                businessImpact: risk.businessImpact,
                stakeholders: risk.stakeholders,
                category: risk.category
              },
              riskNarrative: contextData?.riskNarrative || '',
              businessContext: contextData?.businessContext || '',
              parentAssessmentUuid: contextData?.parentAssessmentUuid,
              usePolicyRAG: templateConfig?.use_control_rag === true,
              policyTypes: templateConfig?.policy_types || ['governance', 'ai-control']
            })
          });

          if (!controlsResponse.ok) {
            console.error(`[NavigatorActionPlanningService] AI generation failed for ${risk.riskName}`);
            continue;
          }

          const responseData = await controlsResponse.json();
          let controls = responseData.controls || [];

          console.log(`[NavigatorActionPlanningService] Received ${controls.length} controls for ${risk.riskName}`);

          if (usePolicyRAG && controls.length > 0) {
            console.log(`[NavigatorActionPlanningService] Matching ${controls.length} controls with policy directives for risk: ${risk.riskName}`);

            try {
              const policyService = new PolicyMatchingService();
              controls = await policyService.enrichControlsWithPolicies(controls, {
                policyTypes: policyTypes,
                riskNarrative: riskNarrative,
                threshold: 0.3,
                topK: 3
              });

              const matchedCount = controls.filter(c => c.policyEvidence?.length > 0).length;
              console.log(`[NavigatorActionPlanningService] ${matchedCount}/${controls.length} controls matched with policy directives`);

              if (this.liveUpdateCallback) {
                this.liveUpdateCallback('policyMatching', {
                  riskName: risk.riskName,
                  totalControls: controls.length,
                  matchedControls: matchedCount,
                  areaKey
                });
              }
            } catch (policyError) {
              console.error(`[NavigatorActionPlanningService] Policy matching error for ${risk.riskName}:`, policyError.message);
            }
          }

          if (controls.length > 0) {
            const topControls = controls.slice(0, 3);

            if (this.liveUpdateCallback) {
              this.liveUpdateCallback('controlsGenerated', {
                riskName: risk.riskName,
                controlsCount: topControls.length,
                actionsGenerated,
                areaKey,
                controls: topControls.map(c => ({
                  controlId: c.controlId,
                  name: c.name || c.title,
                  description: c.description,
                  policyEvidence: c.policyEvidence || [],
                  source: c.source || 'AI_Generated'
                }))
              });
            }

            for (const control of topControls) {
              this.dispatch(saveControl({
                risk: {
                  ...risk,
                  sourceName: risk.category,
                  areaName: risk.areaKey
                },
                control: {
                  ...control,
                  policyEvidence: control.policyEvidence || [],
                  source: control.source || 'AI_Generated',
                  assessmentType: 'navigator'
                },
                modelId: this.modelId,
                assessmentUuid: this.assessmentUuid,
                controlModelType: 'advanced',
                controlModelStage: 'production'
              }));

              const controlEvalCfg = areaDefinition?.controlEvaluationForm || [];
              if (controlEvalCfg.length > 0) {
                const evalResponse = await authFetch('/.netlify/functions/generateControlEvaluationData', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    risk,
                    control,
                    evaluationConfig: controlEvalCfg
                  })
                });

                if (evalResponse.ok) {
                  const evalData = await evalResponse.json();
                  if (evalData.success && evalData.evaluationData) {
                    this.dispatch(updateControlEvaluationField(
                      risk.riskId,
                      control.controlId,
                      'generated_evaluation',
                      evalData.evaluationData
                    ));

                    if (this.liveUpdateCallback) {
                      this.liveUpdateCallback('controlEvaluated', {
                        riskName: risk.riskName,
                        controlName: control.name || control.title,
                        evaluationData: evalData.evaluationData,
                        areaKey
                      });
                    }
                  }
                }
              }

              actionsGenerated++;
            }
          }
        } catch (actionError) {
          console.error(`[NavigatorActionPlanningService] Action planning failed for ${risk.riskName}:`, actionError);
        }
      }

      return {
        evaluatedRisks: evaluatedRisks.length,
        actionsGenerated,
        areaKey
      };

    } catch (error) {
      console.error('[NavigatorActionPlanningService] Action planning failed:', error);
      throw error;
    }
  }
}
